import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { MdKeyboardArrowDown, MdFreeBreakfast } from 'react-icons/md';
import { VscInfo } from 'react-icons/vsc';

import { Button } from 'components/common/button';
import { BookButton } from 'components/bookButton';
import RoomBookButtons from './RoomBookButtons';

import { openModal } from 'features/modalSlice';
import { setSelectedRoom } from '../../features/roomsSlice';
import { setSelectedCancellationAndPaymentPolicyData } from 'features/userSlice';

import { stripHTMLTags, makeStatsRequestThrottled } from '../../lib';

import './Room.css';
import {
    setSelectedRateID,
    setSelectedRoomForm,
    setSelectedFormRoom,
} from 'features/roomBookingFormSlice';
import { RadioGroup } from 'components/common/radioGroup';
import {
    addCartItem,
    setAddingRate,
    setCartOpenState,
    setSmallCartOpenState,
    setTempRate,
} from 'features/cart/cartSlice';
import axios from 'axios';
import { toast } from 'react-toastify';
import { RadioGroupCustom } from 'components/common/radioGroupCustom';
import { apiConfig, WIDGET_STATS_SETTINGS } from 'config';

const Room = ({ room = {}, index, isBestRate }) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(() => index === 0);
    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);

    const roomRef = useRef(null);
    const { currencySymbol, text: bestRateText } = useSelector(
        (state) => state.miniBookingWidget?.data?.ourPrice
    );

    let defaultRate;

    if (Array.isArray(room.rates) && room.rates.length > 0) {
        defaultRate = room.rates[0];
    }

    const openRoomDetails = (roomIndex) => {
        dispatch(setSelectedRoom({ selectedRoomIndex: roomIndex }));
    };
    const rooms = useSelector(
        (state) => state.miniBookingWidget?.data?.ourPrice?.rooms || 1
    );
    const isDirect = useSelector(
        (state) => state.miniBookingWidget?.data?.bookDirectly
    );
    const propCode = useSelector(
        (state) => state.miniBookingWidget?.data?.propCode
    );
    const propertyID = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyID
    );
    const hasCart = useSelector(
        (state) => state.miniBookingWidget?.data?.hasCart
    );
    const cartItems = useSelector((state) => state.cart?.cartItems);
    const isAddingRate = useSelector((state) => state.cart?.isAddingRate);

    const searchOptions = useSelector((state) => state.user);
    const cartID = useSelector(
        (state) => state.miniBookingWidget?.data?.cartID
    );
    let noRedirect = false;
    if (hasCart || isDirect) {
        noRedirect = true;
    }

    const widgetVariant = useSelector(
        (state) => state.miniBookingWidget?.data?.smallWidget
    );

    const thisRoomCartItems = cartItems.filter(
        (item) => item.roomTypeID === room.roomTypeID
    );

    const openRoomBookingForm = async (roomIndex, rateID, room) => {
        if (isAddingRate) {
            return;
        }
        if (hasCart && room) {
            const {
                name: roomName,
                type: roomType,
                rates,
                photos,
                roomTypeID,
            } = room;
            const roomPhoto = photos?.[0]?.large;

            const selectedRate = rates.find((rate) => rate.rateID === rateID);
            if (!selectedRate) return;

            const { pricing, name: rateName } = selectedRate;
            const price = pricing?.price;
            const prePayPrice = pricing?.prePayPrice;

            const isRoomTypeInCart = cartItems.some(
                (item) => item.roomTypeID === roomTypeID
            );
            const isRateInCart = cartItems.some(
                (item) => item.rateID === rateID
            );

            if (isRoomTypeInCart) {
                const existingItem = cartItems.find(
                    (item) =>
                        item.roomTypeID === roomTypeID && item.rateID === rateID
                );

                // If the rate is in the cart, compare the necessary fields
                if (isRateInCart) {
                    if (
                        existingItem &&
                        (existingItem.quantity !==
                            searchOptions?.numberOfRooms ||
                            existingItem.adults !== searchOptions?.adults ||
                            existingItem.children !== searchOptions?.children ||
                            existingItem.price !== price)
                    ) {
                        // Replace the existing rate with the selected rate
                        const updatedCartItem = {
                            ...existingItem,
                            rateID,
                            roomName,
                            roomType,
                            price,
                            roomPhoto,
                            roomTypeID,
                            quantity: searchOptions?.numberOfRooms,
                            adults: searchOptions?.adults,
                            children: searchOptions?.children,
                            checkin: searchOptions?.checkInDay.date,
                            checkout: searchOptions?.checkOutDay.date,
                            rateName,
                            totalPrice:
                                Number(price) *
                                Number(searchOptions?.numberOfRooms),
                            prePayPrice,
                        };

                        dispatch(addCartItem(updatedCartItem));
                        dispatch(setCartOpenState(true));
                    } else {
                        dispatch(setCartOpenState(true));
                    }
                } else {
                    // Handle case where the rate is not in the cart
                    dispatch(setTempRate({ tempRate: rateID, roomTypeID }));
                    dispatch(openModal({ modalId: 'AreYouSureModal' }));
                }
            } else {
                // Handle case where the room type is not in the cart
                if (!isRateInCart) {
                    const cartItem = {
                        rateID,
                        hotelCode: propCode,
                        roomName,
                        roomType,
                        price,
                        roomPhoto,
                        roomTypeID,
                        quantity: searchOptions?.numberOfRooms,
                        adults: searchOptions?.adults,
                        children: searchOptions?.children,
                        checkin: searchOptions?.checkInDay.date,
                        checkout: searchOptions?.checkOutDay.date,
                        cartID,
                        rateName,
                        isCompleted: false,
                        totalPrice:
                            Number(price) *
                            Number(searchOptions?.numberOfRooms),
                        extras: [],
                        prePayPrice,
                    };

                    dispatch(setAddingRate(true));

                    try {
                        const response = await addToCartBack(cartItem);
                        if (response?.status === 'ok') {
                            cartItem.ID = response.ID;
                            dispatch(setSmallCartOpenState(true));
                            dispatch(addCartItem(cartItem));
                        }
                    } finally {
                        dispatch(setAddingRate(false));
                    }
                } else {
                    dispatch(setCartOpenState(true));
                }
            }
        } else if (isDirect) {
            dispatch(setSelectedRoomForm({ selectedRoomFormIndex: roomIndex }));
            dispatch(setSelectedRateID({ selectedRateID: rateID }));
            dispatch(setSelectedFormRoom({ room: room }));
        } else {
            makeStatsRequestThrottled(
                WIDGET_STATS_SETTINGS.actions.booknow,
                propertyID
            );
        }
    };

    const addToCartBack = async (cartItem) => {
        try {
            const response = await axios.post(apiConfig?.addToCartBack, {
                ...cartItem,
            });

            if (response?.data.status.toLowerCase() === 'ok') {
                return response?.data;
            } else {
                toast.error(Lang?.somethingWentWrong, {
                    autoClose: true,
                    theme: 'colored',
                    PlacementType: 'top-left',
                });
                return 'error';
            }
        } catch (error) {
            toast.error(Lang?.networkError, {
                autoClose: true,
                theme: 'colored',
            });
            return 'error';
        }
    };

    const handleInfoIconClick = useCallback((data) => {
        dispatch(setSelectedCancellationAndPaymentPolicyData({ ...data }));
        dispatch(
            openModal({
                modalId: 'CancellationAndPaymentPolicyModal',
            })
        );
    }, []);

    const MAX_CHARS = 160;

    if (!defaultRate) {
        return null;
    }

    const [selectedRate, setSelectedRate] = useState(0);

    useEffect(() => {
        if (thisRoomCartItems && thisRoomCartItems.length > 0) {
            const cartSelectedRate = thisRoomCartItems[0].rateID;
            setSelectedRate(cartSelectedRate);
        }
    }, [thisRoomCartItems]);

    const items = room.rates.map((rate, index) => {
        let isInCart = false;
        let noClick = false;
        if (thisRoomCartItems && thisRoomCartItems.length > 0) {
            isInCart = cartItems?.filter((item) => item.rateID === rate.rateID);
            if (!isInCart || isInCart?.length === 0) {
                noClick = true;
            }
        }

        const isAvailable =
            rate?.availableRooms?.length > 0 &&
            rate?.memberBookNowText !== 'Not Avail';
        const notPickedClass =
            rate?.rateID === selectedRate || (index === 0 && selectedRate === 0)
                ? ''
                : 'ab-not-picked-rate';

        const label = (
            <div
                className={`ab-room-extra-offer ${
                    !isAvailable ? 'not-available' : ''
                }`}
                key={index}
            >
                <div
                    className={`ab-room-extra-offer-name ${
                        widgetVariant === 'openBigDrawer' ? 's-full-width' : ''
                    }`}
                >
                    <div>
                        <div className="ab-room-offer-title">
                            <span
                                style={{
                                    marginRight: '5px',
                                }}
                            >
                                {rate.rate}
                            </span>
                            <Button
                                color="black"
                                className="ab-btn-info"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    handleInfoIconClick({
                                        cancellationPolicyType:
                                            rate?.cancelationPolicyType,
                                        cancellationPolicy:
                                            rate?.cancelationPolicy,
                                        cancellationPenalty:
                                            rate?.cancelationPenalty,
                                        paymentPolicy: rate?.paymentPolicy,
                                        modalTitle: 'Cancellation policy',
                                    });
                                }}
                                icon={<VscInfo size={13} />}
                            ></Button>
                        </div>
                        <div className="ab-room-offer-notes">
                            {stripHTMLTags(rate.cancelationPolicy).length >
                                MAX_CHARS &&
                                stripHTMLTags(rate.cancelationPolicy).substr(
                                    0,
                                    MAX_CHARS
                                ) + '...'}

                            {stripHTMLTags(rate.cancelationPolicy).length <=
                                MAX_CHARS &&
                                stripHTMLTags(rate.cancelationPolicy)}
                        </div>
                    </div>
                </div>
                <div className="ab-room-extra-offer-price">
                    <div className="ab-room-extra-offer-price-inner">
                        <div className="ab-room-offer-pricestext">
                            {Lang?.priceAdultsText}
                        </div>
                        {rate?.pricing?.memberPrice ? (
                            <>
                                <div className="ab-room-offer-prices">
                                    <span className="ab-old-price">
                                        {rooms > 1 && `${rooms} x `}
                                        {rate.pricing.price} {currencySymbol}
                                    </span>{' '}
                                    {rate.pricing.memberPrice} {currencySymbol}
                                </div>
                                <div className="ab-room-offer-notes">
                                    <div className="ab-save-cls">
                                        {rate.pricing.saveText}{' '}
                                        <span className="ab-discount">
                                            {rate.pricing.savePrice}{' '}
                                            {currencySymbol}
                                        </span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {rate?.pricing?.price !== 0 && (
                                    <div className="ab-room-offer-prices">
                                        {rooms > 1 && `${rooms} x `}{' '}
                                        {rate.pricing.price} {currencySymbol}
                                    </div>
                                )}
                            </>
                        )}
                        {/* <div className="ab-room-offer-notes">
                                            {rate.hasMinStay && (
                                                <div className="ab-room-upper-minstay">
                                                    Min-Stay
                                                </div>
                                            )}
                                        </div> */}
                    </div>
                </div>
                <div className={`ab-room-extra-offer-button ${notPickedClass}`}>
                    <RoomBookButtons
                        price={rate.pricing.price}
                        currencySymbol={currencySymbol}
                        isRoomAvailable={isAvailable}
                        Lang={Lang}
                        rate={rate}
                        onClick={() =>
                            openRoomBookingForm(index, rate?.rateID, room)
                        }
                        isDirect={isDirect ? 1 : 0}
                        noRedirect={noRedirect}
                        isLoading={isAddingRate}
                    />
                </div>
            </div>
        );

        return {
            id: rate.rateID,
            value: rate.rateID,
            label: label,
            noClick: noClick,
            room: room,
        };
    });

    return (
        <div
            ref={roomRef}
            id={index}
            className={`ab-room-row ${
                widgetVariant === 'openBigDrawer' ? 'full-width' : ''
            } ${isOpen ? 'expanded' : ''}`}
        >
            <div className="ab-room-row-main">
                <div
                    className="ab-room-image-container"
                    onClick={() => openRoomDetails(index)}
                >
                    <div
                        className="ab-room-image"
                        style={{
                            backgroundImage: `url("${room?.url?.photoL}")`,
                        }}
                    ></div>
                    {room?.photos?.length > 0 && (
                        <div className="ab-more-photos">
                            +{room.photos.length} {room.photoText}
                        </div>
                    )}
                </div>
                <div className="ab-room-middle-container">
                    <div
                        className="ab-room-middle-upper"
                        onClick={() => openRoomDetails(index)}
                    >
                        <div className="ab-room-upper-head">
                            <div className="ab-room-upper-name">
                                {room?.name}
                            </div>
                            <div
                                className="ab-room-upper-description"
                                dangerouslySetInnerHTML={{
                                    __html: defaultRate?.rateDescription,
                                }}
                            ></div>
                        </div>
                        <div className="ab-room-upper-notes">
                            <div className="ab-room-upper-save ab-save-cls ab-save-box">
                                {defaultRate.pricing.inThisStayText}
                            </div>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingTop: '5px',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <div className="ab-room-upper-occupancy">
                                    {room?.remainingText}
                                </div>
                                {defaultRate?.hasBreakfast && (
                                    <div
                                        className="ab-freebies"
                                        style={{ marginLeft: '20px' }}
                                    >
                                        <div className="ab-freebie-new">
                                            <MdFreeBreakfast size={20} />
                                            <span className="ab-breakfast-text-new">
                                                {Lang.breakfastText}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {room?.minStaySort > 0 && (
                                <div className="ab-room-upper-minstay">
                                    Min-Stay
                                </div>
                            )}
                        </div>
                    </div>
                    {/* Do not take into account the default rate which is the first one */}
                    {room?.rates?.length >= 1 && (
                        <div className="ab-room-middle-footer">
                            <div className="ab-expand-options">
                                <div
                                    className="ab-show-more"
                                    onClick={() => {
                                        if (roomRef.current) {
                                            roomRef.current.classList.add(
                                                'expanded'
                                            );
                                        }
                                        setIsOpen(true);
                                    }}
                                >
                                    <span className="icon">
                                        <MdKeyboardArrowDown size={20} />
                                    </span>
                                    <span>{room.seeMoreOffers}</span>
                                    <span className="ab-moreRates-price">
                                        {room?.seeMoreOffersPrice}
                                    </span>
                                </div>
                                <div
                                    className="ab-show-less"
                                    onClick={() => {
                                        if (roomRef.current) {
                                            roomRef.current.classList.remove(
                                                'expanded'
                                            );
                                        }
                                        setIsOpen(false);
                                    }}
                                >
                                    <span className="icon">
                                        <MdKeyboardArrowDown size={20} />
                                    </span>
                                    <span>{room.seeLessOffers}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div
                    className={`ab-room-right-container ${
                        room?.roomAvail === 0 ? 'sold-out' : ''
                    }`}
                >
                    {room?.roomAvail > 0 ? (
                        <>
                            {isBestRate && (
                                <div className="ab-bestprice-ribbon">
                                    {bestRateText}
                                </div>
                            )}

                            <div className="ab-price-section">
                                <div className="ab-priceadultstext">
                                    {Lang?.priceAdultsText}
                                </div>
                                {defaultRate.pricing?.memberPrice ? (
                                    <>
                                        <div className="ab-old-price">
                                            {defaultRate.pricing.price}{' '}
                                            {currencySymbol}
                                        </div>
                                        <div
                                            className="ab-price"
                                            tabIndex="0"
                                            role="button"
                                        >
                                            {rooms > 1 && `${rooms} x `}
                                            {
                                                defaultRate?.pricing.memberPrice
                                            }{' '}
                                            {currencySymbol}
                                        </div>
                                        <div className="ab-providerWrap">
                                            <div className="ab-provider">
                                                {defaultRate.pricing.saveText}{' '}
                                                <span className="ab-discount">
                                                    {
                                                        defaultRate.pricing
                                                            .savePrice
                                                    }{' '}
                                                    {currencySymbol}
                                                </span>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            className="ab-price"
                                            tabIndex="0"
                                            role="button"
                                        >
                                            {rooms > 1 && `${rooms} x `}
                                            {defaultRate?.pricing.price}{' '}
                                            {currencySymbol}
                                        </div>
                                    </>
                                )}

                                {/* {defaultRate?.cancelationPolicy && (
                                    <div className="ab-freebies">
                                        <div className="ab-freebie">
                                            {defaultRate.cancelationPolicyType}
                                            {defaultRate?.freeCancelationText && (
                                                <div className="ab-cancel-policy-text">
                                                    {
                                                        defaultRate.freeCancelationText
                                                    }
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )} */}

                                <div className="ab-freebies">
                                    <div className="ab-freebie">
                                        {defaultRate?.rate && (
                                            <Button
                                                styles={{ 'font-size': '13px' }}
                                                color="black"
                                                className="ab-btn-info"
                                                size={13}
                                                alignItems="flex-start"
                                                onClick={() =>
                                                    handleInfoIconClick({
                                                        cancellationPolicyType:
                                                            defaultRate?.cancelationPolicyType,
                                                        cancellationPolicy:
                                                            defaultRate?.cancelationPolicy,
                                                        cancellationPenalty:
                                                            defaultRate?.cancelationPenalty,
                                                        paymentPolicy:
                                                            defaultRate?.paymentPolicy,
                                                        modalTitle:
                                                            'Cancellation policy',
                                                    })
                                                }
                                                icon={<VscInfo size={13} />}
                                            >
                                                <span
                                                    style={{
                                                        marginRight: '5px',
                                                    }}
                                                >
                                                    <div
                                                        className="ab-cancel-policy-text"
                                                        style={{
                                                            textAlign: 'left',
                                                            whiteSpace:
                                                                'normal',
                                                        }}
                                                    >
                                                        {defaultRate?.rate}
                                                    </div>
                                                </span>
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <BookButton
                                currencySymbol={currencySymbol}
                                price={`${defaultRate?.pricing?.price}`}
                                text={
                                    noRedirect
                                        ? Lang?.select
                                        : defaultRate?.availableRooms[0]
                                              ?.BookNowText
                                }
                                url={defaultRate?.availableRooms[0]?.bookNowUrl}
                                variant="non-member"
                                onClick={() =>
                                    openRoomBookingForm(
                                        index,
                                        defaultRate?.rateID,
                                        room
                                    )
                                }
                                isDirect={isDirect === 1}
                                noRedirect={noRedirect}
                                isLoading={isAddingRate}
                            />
                            <BookButton
                                currencySymbol={currencySymbol}
                                price={`${defaultRate?.pricing?.memberPrice}`}
                                text={
                                    noRedirect
                                        ? Lang?.select
                                        : defaultRate?.availableRooms[0]
                                              ?.memberBookNowText
                                }
                                url={
                                    defaultRate?.availableRooms[0]
                                        ?.memberBookNowUrl
                                }
                                variant="member"
                                onClick={() =>
                                    openRoomBookingForm(
                                        index,
                                        defaultRate?.rateID,
                                        room
                                    )
                                }
                                isDirect={isDirect === 1}
                                noRedirect={noRedirect}
                                isLoading={isAddingRate}
                            />
                        </>
                    ) : room?.rates[0]?.memberBookNowText === 'Not Avail' ? (
                        <span>{Lang.notAvail}</span>
                    ) : (
                        <span>{Lang.soldOut}</span>
                    )}
                </div>
            </div>

            {room?.rates?.length >= 1 && isOpen && (
                <div className="ab-room-more-offers">
                    {hasCart ? (
                        <RadioGroupCustom
                            items={items}
                            setSelectedRate={setSelectedRate}
                            selectedRate={selectedRate}
                        />
                    ) : (
                        <RadioGroup
                            items={items}
                            setSelectedRate={setSelectedRate}
                            selectedRate={selectedRate}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

Room.propTypes = {
    room: PropTypes.object,
    index: PropTypes.number,
    isBestRate: PropTypes.bool,
};

export default Room;
